import murmurhash from '@emotion/hash';
import { createContext, forwardRef, createElement, useSyncExternalStore, useMemo, useCallback, useId, useEffect, useContext } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector.js';

// src/helpers.ts
var first = (value) => value[0];
var last = (value) => value[value.length - 1];
var identity = (value) => value;
var noop = () => {
};
var isNonEmpty = (value) => value !== "";
var isParam = (value) => value.startsWith(":");
var ensureSlashPrefix = (value) => value[0] === "/" ? value : "/" + value;
var areParamsArrayEqual = (arrayA, arrayB) => arrayA.length === arrayB.length && arrayA.every((a, index) => a === arrayB[index]);
var getStableParamsKey = (params) => {
  const keys = Object.keys(params);
  return keys.length > 0 ? JSON.stringify(keys.sort().map((key) => [key, params[key]])) : "";
};
var getRouteKey = (name, pathParams, searchParams) => {
  const stableStart = name + getStableParamsKey(pathParams);
  const stableEnd = getStableParamsKey(searchParams);
  return `${murmurhash(stableStart)}-${murmurhash(stableEnd)}`;
};
var areRouteEqual = (routeA, routeB) => (routeA == null ? void 0 : routeA.key) === (routeB == null ? void 0 : routeB.key);
var extractParamNameUnion = (paramName) => {
  const bracketIndex = paramName.indexOf("{");
  if (bracketIndex > -1 && paramName.endsWith("}")) {
    return {
      name: paramName.substring(0, bracketIndex),
      union: paramName.substring(bracketIndex + 1, paramName.length - 1).split("|").filter(isNonEmpty)
    };
  } else {
    return { name: paramName };
  }
};

// src/concatRoutes.ts
var addPrefixOnNonEmpty = (value, prefix) => value === "" ? value : prefix + value;
var concatRoutes = (routeA, routeB) => {
  const prefixedPathA = ensureSlashPrefix(routeA["path"]);
  const prefixedPathB = ensureSlashPrefix(routeB["path"]);
  const unprefixedSearchA = routeA.search.substring(1);
  const unprefixedSearchB = routeB.search.substring(1);
  const path = prefixedPathA === "/" ? prefixedPathB : prefixedPathB === "/" ? prefixedPathA : prefixedPathA + prefixedPathB;
  const search = unprefixedSearchA === "" ? unprefixedSearchB : unprefixedSearchA + addPrefixOnNonEmpty(unprefixedSearchB, "&");
  return path + addPrefixOnNonEmpty(search, "?");
};

// src/search.ts
var decodeSearch = (search) => {
  const params = new URLSearchParams(search);
  const output = {};
  for (const [key, value] of params) {
    const existing = output[key];
    if (existing != null) {
      output[key] = typeof existing === "string" ? [existing, value] : existing.concat(value);
    } else {
      output[key] = value;
    }
  }
  return output;
};
var NO_VALUE_PARAM_REGEXP = /=&/g;
var FINISH_BY_EQUAL_REGEXP = /=$/g;
var encodeSearch = (search) => {
  const keys = Object.keys(search);
  if (keys.length === 0) {
    return "";
  }
  const params = new URLSearchParams();
  keys.sort();
  for (const key of keys) {
    const value = search[key];
    if (value == null) {
      continue;
    }
    if (typeof value === "string") {
      params.append(key, value);
    } else {
      for (const item of value) {
        params.append(key, item);
      }
    }
  }
  const output = params.toString().replace(NO_VALUE_PARAM_REGEXP, "&").replace(FINISH_BY_EQUAL_REGEXP, "");
  if (output === "") {
    return "";
  }
  return "?" + output;
};

// src/history.ts
var initialLocationHasChanged = false;
var parseRoute = (route) => {
  const hashIndex = route.indexOf("#");
  const cleanRoute = ensureSlashPrefix(
    hashIndex < 0 ? route : route.substring(0, hashIndex)
  );
  const searchIndex = cleanRoute.indexOf("?");
  if (searchIndex < 0) {
    return { path: cleanRoute, search: "" };
  }
  return {
    path: cleanRoute.substring(0, searchIndex),
    search: cleanRoute.substring(searchIndex)
  };
};
var decodeLocation = (input) => {
  const route = parseRoute(input);
  const path = route.path.substring(1);
  const parsedPath = path !== "" ? initialLocationHasChanged ? path.split("/").map(decodeURIComponent) : path.split("/").filter(isNonEmpty).map(decodeURIComponent) : [];
  const parsedSearch = route.search !== "" ? decodeSearch(route.search) : {};
  const rawPath = "/" + parsedPath.map(encodeURIComponent).join("/");
  const rawSearch = encodeSearch(parsedSearch);
  const stringifiedLocation = rawPath + rawSearch;
  return {
    path: parsedPath,
    search: parsedSearch,
    raw: {
      path: rawPath,
      search: rawSearch
    },
    toString() {
      return stringifiedLocation;
    }
  };
};
var onBeforeUnload = (event) => {
  event.preventDefault();
  event.returnValue = "";
};
var createBrowserHistory = () => {
  const listeners = /* @__PURE__ */ new Set();
  let blockers = [];
  const globalHistory = window.history;
  const globalLocation = window.location;
  let currentLocation = decodeLocation(
    globalLocation.pathname + globalLocation.search
  );
  const maybeUpdateLocation = () => {
    const nextLocation = decodeLocation(
      globalLocation.pathname + globalLocation.search
    );
    if (nextLocation.toString() === currentLocation.toString()) {
      return;
    }
    initialLocationHasChanged = true;
    const searchHasChanged = nextLocation.raw.search !== currentLocation.raw.search;
    const search = searchHasChanged ? {} : currentLocation.search;
    if (searchHasChanged) {
      for (const key in nextLocation.search) {
        if (Object.prototype.hasOwnProperty.call(nextLocation.search, key)) {
          const value = nextLocation.search[key];
          if (value == null) {
            continue;
          }
          const prevValue = currentLocation.search[key];
          if (prevValue == null || typeof prevValue === "string" || typeof value === "string" || !areParamsArrayEqual(value, prevValue)) {
            search[key] = value;
          } else {
            search[key] = prevValue;
          }
        }
      }
    }
    currentLocation = {
      path: nextLocation.raw.path !== currentLocation.raw.path ? nextLocation.path : currentLocation.path,
      search,
      raw: nextLocation.raw,
      toString: nextLocation.toString
    };
    listeners.forEach((listener) => listener(currentLocation));
  };
  window.addEventListener("popstate", () => {
    maybeUpdateLocation();
  });
  return {
    get location() {
      return currentLocation;
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    push: (url) => {
      const blocker = last(blockers);
      if (blocker == null || window.confirm(blocker.message)) {
        try {
          globalHistory.pushState(null, "", url);
        } catch {
          globalLocation.assign(url);
        }
        maybeUpdateLocation();
      }
    },
    replace: (url) => {
      const blocker = last(blockers);
      if (blocker == null || window.confirm(blocker.message)) {
        globalHistory.replaceState(null, "", url);
        maybeUpdateLocation();
      }
    },
    block: (blocker) => {
      blockers.push(blocker);
      if (blockers.length === 1) {
        window.addEventListener("beforeunload", onBeforeUnload, {
          capture: true
        });
      }
      return () => {
        blockers = blockers.filter(({ id }) => id !== blocker.id);
        if (blockers.length === 0) {
          window.removeEventListener("beforeunload", onBeforeUnload, {
            capture: true
          });
        }
      };
    }
  };
};
var history = typeof window !== "undefined" ? createBrowserHistory() : {
  location: decodeLocation("/"),
  subscribe: () => noop,
  push: noop,
  replace: noop,
  block: () => noop
};
var getLocation = () => history.location;
var subscribeToLocation = history.subscribe;
var pushUnsafe = history.push;
var replaceUnsafe = history.replace;
var block = history.block;
var GetUniversalLocationContext = createContext(getLocation);
var GetUniversalLocationProvider = GetUniversalLocationContext.Provider;
var useGetUniversalLocation = () => useContext(GetUniversalLocationContext);
var useLocation = () => {
  const getUniversalLocation = useGetUniversalLocation();
  return useSyncExternalStore(
    subscribeToLocation,
    getUniversalLocation,
    getUniversalLocation
  );
};
var hasInitialLocationChanged = () => initialLocationHasChanged;

// src/createGroup.ts
var createGroup = (name, baseRoute, routes) => {
  const baseRouteObject = parseRoute(baseRoute);
  const output = {};
  for (const key in routes) {
    if (Object.prototype.hasOwnProperty.call(routes, key)) {
      output[name + key] = concatRoutes(
        baseRouteObject,
        parseRoute(routes[key])
      );
    }
  }
  return output;
};

// src/matcher.ts
var extractFromPath = (path) => {
  const parts = path.split("/").filter(isNonEmpty);
  const output = [];
  let ranking = parts.length > 0 ? parts.length * 5 : 6;
  for (const part of parts) {
    if (isParam(part)) {
      const param = extractParamNameUnion(part.substring(1));
      ranking += param.union == null ? 2 : 3;
      output.push(param);
    } else {
      ranking += 4;
      output.push(encodeURIComponent(part));
    }
  }
  return { ranking, path: output };
};
var getMatcher = (name, route) => {
  const parsed = parseRoute(route);
  const isArea = parsed.path.endsWith("/*");
  const { ranking, path } = extractFromPath(
    isArea ? parsed.path.slice(0, -2) : parsed.path
  );
  const matcher = {
    isArea,
    name,
    // penality due to wildcard
    ranking: isArea ? ranking - 1 : ranking,
    path,
    search: void 0
  };
  if (parsed.search !== "") {
    matcher.search = {};
    const params = new URLSearchParams(parsed.search);
    for (const [key] of params) {
      if (isParam(key)) {
        const multiple = key.endsWith("[]");
        const { name: name2, union } = extractParamNameUnion(
          key.substring(1, key.length - (multiple ? 2 : 0))
        );
        matcher.search[name2] = union == null ? { multiple } : { multiple, union };
      }
    }
  }
  return matcher;
};
var getMatchResult = (location, matcher) => {
  if (!matcher.isArea && location.path.length !== matcher.path.length || matcher.isArea && location.path.length < matcher.path.length) {
    return;
  }
  const pathParams = {};
  const searchParams = {};
  for (let index = 0; index < matcher.path.length; index++) {
    const part = location.path[index];
    const test = matcher.path[index];
    if (test == null) {
      continue;
    }
    if (typeof test === "string") {
      if (part === test) {
        continue;
      } else {
        return;
      }
    }
    if (part == null) {
      return;
    }
    const { name, union } = test;
    if (union == null || union.includes(part)) {
      pathParams[name] = part;
    } else {
      return;
    }
  }
  for (const key in matcher.search) {
    if (Object.prototype.hasOwnProperty.call(matcher.search, key)) {
      const part = location.search[key];
      const test = matcher.search[key];
      if (part == null || test == null) {
        continue;
      }
      const { multiple, union } = test;
      const parts = typeof part === "string" ? [part] : part;
      const values = union == null ? parts : parts.filter((item) => union.includes(item));
      if (multiple) {
        searchParams[key] = values;
        continue;
      }
      const value = values[0];
      if (value != null) {
        searchParams[key] = value;
      }
    }
  }
  return {
    key: getRouteKey(matcher.name, pathParams, searchParams),
    name: matcher.name,
    params: { ...pathParams, ...searchParams }
  };
};
var match = (location, matchers) => {
  for (const matcher of matchers) {
    const result = getMatchResult(location, matcher);
    if (result != null) {
      return result;
    }
  }
};
var matchToUrl = (matcher, params = {}) => {
  const path = "/" + matcher.path.map(
    (part) => encodeURIComponent(
      typeof part === "string" ? part : String(params[part.name])
    )
  ).join("/");
  let search = "";
  if (matcher.search != null) {
    const object = {};
    for (const key in params) {
      const param = params[key];
      const test = matcher.search[key];
      if (param == null || test == null) {
        continue;
      }
      const { union } = test;
      if (typeof param === "string") {
        if (union == null || union.includes(param)) {
          object[key] = param;
        }
      } else {
        object[key] = union == null ? param : param.filter((item) => union.includes(item));
      }
    }
    search = encodeSearch(object);
  }
  return path + search;
};

// src/createRouter.ts
var createRouter = (routes, options = {}) => {
  const { basePath = "" } = options;
  const basePathObject = {
    path: parseRoute(basePath).path,
    search: ""
    // search is not supported in basePath
  };
  const matchers = {};
  const rankedMatchers = [];
  for (const routeName in routes) {
    if (Object.prototype.hasOwnProperty.call(routes, routeName)) {
      const matcher = getMatcher(
        routeName,
        basePath !== "" ? concatRoutes(basePathObject, parseRoute(routes[routeName])) : routes[routeName]
      );
      matchers[routeName] = matcher;
      rankedMatchers.push(matcher);
    }
  }
  rankedMatchers.sort(
    (matcherA, matcherB) => matcherB.ranking - matcherA.ranking
  );
  const P = {};
  const createURLFns = {};
  for (const matcher of rankedMatchers) {
    const name = matcher.name;
    P[name] = (params) => ({ name, params });
    if (!matcher.isArea) {
      const finiteName = name;
      createURLFns[finiteName] = (params) => matchToUrl(matchers[finiteName], params);
    }
  }
  const useRoute = (routeNames) => {
    const matchersKey = JSON.stringify(routeNames);
    const matchers2 = useMemo(
      () => rankedMatchers.filter(
        ({ name }) => routeNames.includes(name)
      ),
      [matchersKey]
      // eslint-disable-line react-hooks/exhaustive-deps
    );
    const getUniversalLocation = useGetUniversalLocation();
    const getMatch = () => match(getUniversalLocation(), matchers2);
    return useSyncExternalStoreWithSelector(
      subscribeToLocation,
      getMatch,
      getMatch,
      identity,
      areRouteEqual
    );
  };
  const getRoute = (routeNames, location) => {
    const locationObject = location != null ? decodeLocation(location) : getLocation();
    const matchers2 = rankedMatchers.filter(
      ({ name }) => routeNames.includes(name)
    );
    return match(locationObject, matchers2);
  };
  const push = (routeName, ...params) => pushUnsafe(matchToUrl(matchers[routeName], first(params)));
  const replace = (routeName, ...params) => replaceUnsafe(matchToUrl(matchers[routeName], first(params)));
  return {
    useRoute,
    getRoute,
    push,
    replace,
    P,
    ...createURLFns
  };
};
var useLinkProps = ({
  href,
  replace = false,
  target
}) => {
  const hrefPath = useMemo(() => parseRoute(href).path, [href]);
  const getUniversalLocation = useGetUniversalLocation();
  const getPath = () => hrefPath === getUniversalLocation().raw.path;
  const active = useSyncExternalStore(subscribeToLocation, getPath, getPath);
  const shouldReplace = replace || active;
  const shouldIgnoreTarget = target == null || target === "_self";
  return {
    active,
    onClick: useCallback(
      (event) => {
        if (!event.defaultPrevented && shouldIgnoreTarget && // Let browser handle "target=_blank" etc.
        event.button === 0 && // Ignore everything but left clicks
        !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)) {
          event.preventDefault();
          if (shouldReplace) {
            replaceUnsafe(href);
          } else {
            pushUnsafe(href);
          }
        }
      },
      [shouldReplace, shouldIgnoreTarget, href]
    )
  };
};

// src/Link.ts
var Link = forwardRef(
  ({
    onClick: baseOnClick,
    className,
    replace,
    style,
    target,
    to,
    activeClassName,
    activeStyle,
    ...props
  }, forwardedRef) => {
    const { active, onClick } = useLinkProps({ href: to, replace, target });
    return createElement("a", {
      ...props,
      ref: forwardedRef,
      href: to,
      onClick: (event) => {
        baseOnClick == null ? void 0 : baseOnClick(event);
        onClick(event);
      },
      target,
      className: !active || activeClassName == null ? className : className == null ? activeClassName : `${className} ${activeClassName}`,
      style: !active || activeStyle == null ? style : style == null ? activeStyle : { ...style, ...activeStyle }
    });
  }
);
Link.displayName = "Link";
var ServerUrlProvider = ({ children, value }) => {
  const location = decodeLocation(value);
  return createElement(GetUniversalLocationProvider, {
    children,
    value: () => location
  });
};
var useBlocker = (blocked, message) => {
  const id = useId();
  useEffect(() => {
    if (blocked) {
      return block({ id, message });
    }
  });
};
var focusableElements = {
  A: true,
  INPUT: true,
  SELECT: true,
  TEXTAREA: true
};
var useFocusReset = ({
  route,
  containerRef
}) => {
  const focusKey = route == null ? void 0 : route.key.split("-")[0];
  useEffect(() => {
    const element = containerRef.current;
    if (element != null && hasInitialLocationChanged()) {
      try {
        if (element.getAttribute("tabIndex") == null && !focusableElements[element.nodeName]) {
          element.setAttribute("tabIndex", "-1");
        }
        element.focus();
      } catch {
      }
    }
  }, [containerRef, focusKey]);
};

export { Link, ServerUrlProvider, createGroup, createRouter, decodeSearch, encodeSearch, getLocation, pushUnsafe, replaceUnsafe, subscribeToLocation, useBlocker, useFocusReset, useLinkProps, useLocation };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map